'use client';
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useId, useRef } from "react";
import { ClassNameConfigurator } from "@mui/base";
import { Select as BaseSelect } from "@mui/base/Select";
import { autoPlacement as floatingAutoPlacement, size as floatingSize } from "@floating-ui/dom";
import { cva } from "class-variance-authority";
import { cn } from "../../common/helpers.js";
import { Icon } from "../Icon/index.js";
import { errorClasses, helperClasses, labelClasses, requiredClasses } from "../../common/classes.js";
const inputClasses = cva([
    'flex',
    'flex-row',
    'gap-2',
    'items-center',
    'justify-between',
    'w-full',
    'outline',
    'outline-1',
    'px-4',
    'py-2',
    'bg-white',
    'focus:outline-2',
    'rounded-lg',
    'font-medium',
    'transition-[outline-color]',
    'duration-200'
], {
    variants: {
        size: {
            sm: [
                'h-10'
            ],
            md: [
                'h-[3.75rem]'
            ]
        },
        disabled: {
            true: [
                'text-black/40',
                'bg-gray-100',
                'cursor-not-allowed'
            ],
            false: [
                'text-gray-700'
            ]
        },
        error: {
            true: [
                'outline-danger',
                'hover:enabled:outline-danger',
                'hover:focus:enabled:outline-danger'
            ],
            false: [
                'outline-gray-200',
                'focus:outline-primary',
                'hover:focus:enabled:outline-primary'
            ]
        },
        open: {
            true: [],
            false: []
        }
    },
    compoundVariants: [
        {
            error: false,
            open: true,
            className: [
                'outline-2',
                'outline-primary'
            ]
        },
        {
            error: true,
            open: true,
            className: [
                'outline-2',
                'outline-danger'
            ]
        },
        {
            error: false,
            open: false,
            className: [
                'hover:enabled:outline-gray-700'
            ]
        }
    ]
});
export const listboxClasses = cva([
    'bg-white',
    'rounded-lg',
    'w-full',
    'border',
    'border-gray-200',
    'my-2',
    'font-medium',
    'text-gray-700'
]);
const SelectButton = /*#__PURE__*/ forwardRef((props, ref)=>{
    const { ownerState } = props, other = _object_without_properties(props, [
        "ownerState"
    ]);
    return /*#__PURE__*/ _jsxs("button", _object_spread_props(_object_spread({
        type: "button"
    }, other), {
        ref: ref,
        children: [
            other.children,
            /*#__PURE__*/ _jsx(Icon, {
                icon: "chevron-down"
            })
        ]
    }));
});
/**
 * Select - [API](https://scl.sharp.com/?path=/docs/base-select) | [DSM](https://sharphealthcare.notion.site/Inputs-4a77e695a529479fa701e2f3dcdc47e7#49e2612563204c958d34ed4ef4ee92aa)
 */ const Select = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { helperText, label, id, error, size = 'md', className } = _param, props = _object_without_properties(_param, [
        "helperText",
        "label",
        "id",
        "error",
        "size",
        "className"
    ]);
    const generatedID = useId();
    const inputID = id !== null && id !== void 0 ? id : generatedID;
    const buttonRef = useRef();
    return /*#__PURE__*/ _jsxs("div", {
        className: cn('space-y-2', className),
        children: [
            label && /*#__PURE__*/ _jsx("label", {
                htmlFor: inputID,
                className: cn(labelClasses({
                    disabled: Boolean(props.disabled),
                    error: Boolean(error)
                })),
                children: label
            }),
            props.required && /*#__PURE__*/ _jsx("div", {
                "aria-label": "required",
                className: cn(requiredClasses({
                    disabled: Boolean(props.disabled)
                })),
                children: "*"
            }),
            helperText && /*#__PURE__*/ _jsx("div", {
                className: cn(helperClasses()),
                children: helperText
            }),
            /*#__PURE__*/ _jsx(ClassNameConfigurator, {
                disableDefaultClasses: true,
                children: /*#__PURE__*/ _jsx(BaseSelect, _object_spread_props(_object_spread({}, props), {
                    ref: ref,
                    multiple: false,
                    id: inputID,
                    slots: {
                        root: SelectButton
                    },
                    slotProps: {
                        root: (inputState)=>({
                                className: cn(inputClasses({
                                    size,
                                    error: Boolean(error),
                                    disabled: Boolean(inputState.disabled),
                                    open: Boolean(inputState.open)
                                })),
                                ref: buttonRef,
                                'aria-label': label
                            }),
                        listbox: {
                            className: cn(listboxClasses())
                        },
                        popup: {
                            middleware: [
                                floatingAutoPlacement({
                                    allowedPlacements: [
                                        'top',
                                        'bottom'
                                    ]
                                }),
                                floatingSize({
                                    apply: ({ rects, elements })=>{
                                        Object.assign(elements.floating.style, {
                                            minWidth: 'fit-content',
                                            width: `${rects.reference.width}px`,
                                            zIndex: 1301
                                        });
                                    }
                                })
                            ]
                        }
                    }
                }))
            }),
            error && /*#__PURE__*/ _jsx("div", {
                className: cn(errorClasses()),
                children: error
            })
        ]
    });
});
export { Select };
